// Batch Label Print — lets supervisors/managers pre-print labels in bulk
// for a finished product without running a production session. Each box gets
// a unique GS1-128 barcode (serial number increments by 1 per box).

import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Package, CheckCircle2, XCircle, Printer } from 'lucide-react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { getPrintErrorMessage } from '../../../utils/printErrors';
import { useAuth } from '../../../hooks/useAuth';
import productionService from '../../../services/productionService';
import barcodeService from '../../../services/barcodeService';
import { getDateCode } from '../../../utils/dateUtils';
import { getPrintServiceUrl } from '../../../services/operatorService';

interface Props {
  labelCopies: number;
  simulatePrinter: boolean;
}

interface PrinterOption {
  key: string;
  nickname: string;
  ip: string;
}

const BatchLabelPrintSection: React.FC<Props> = ({ labelCopies, simulatePrinter }) => {
  const { t } = useLanguage();
  const { user } = useAuth();

  // Form state
  const [selectedProduct, setSelectedProduct] = useState('');
  const [numberOfBoxes, setNumberOfBoxes] = useState(1);
  const [selectedDateTime, setSelectedDateTime] = useState(() => {
    const now = new Date();
    // Format as YYYY-MM-DDTHH:MM for datetime-local input
    const y = now.getFullYear();
    const mo = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const h = String(now.getHours()).padStart(2, '0');
    const mi = String(now.getMinutes()).padStart(2, '0');
    return `${y}-${mo}-${d}T${h}:${mi}`;
  });
  const [selectedPrinter, setSelectedPrinter] = useState('');

  // Print state
  const [isPrinting, setIsPrinting] = useState(false);
  const [currentBox, setCurrentBox] = useState(0);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  // Fetch finished products
  const { data: products = [] } = useQuery(
    'batch-label-products',
    () => productionService.getProducts(),
    { staleTime: 10 * 60 * 1000 }
  );

  // Fetch registered printers (same pattern as PrinterTestPanel)
  const { data: printers = [] } = useQuery<PrinterOption[]>(
    'batch-label-printers',
    async () => {
      const all = await productionService.getConfigSettings();
      const ipMap: Record<string, string> = {};
      all.filter((s: any) => s.setting_key.endsWith('_ip') && s.setting_key.startsWith('printer_'))
        .forEach((s: any) => { ipMap[s.setting_key.replace(/_ip$/, '')] = s.setting_value; });

      return all
        .filter((s: any) =>
          s.setting_key.startsWith('printer_') &&
          s.setting_key !== 'printer_subnet' &&
          !s.setting_key.endsWith('_ip')
        )
        .filter((s: any) => ipMap[s.setting_key])
        .map((s: any) => ({
          key: s.setting_key,
          nickname: s.setting_key.replace(/^printer_/, ''),
          ip: ipMap[s.setting_key],
        }));
    },
    { refetchOnMount: 'always' }
  );

  const product = products.find((p: any) => p.item_number === selectedProduct);
  const printer = printers.find(p => p.key === selectedPrinter);
  const totalLabels = numberOfBoxes * labelCopies;
  const canPrint = product && printer && numberOfBoxes >= 1 && !isPrinting;

  const handleBatchPrint = async () => {
    if (!product || !printer) return;

    setIsPrinting(true);
    setCurrentBox(0);
    setResult(null);

    const dt = new Date(selectedDateTime);
    const dateCode = getDateCode(selectedDateTime.split('T')[0]);

    // Base serial from HHMMSS as integer
    const hours = String(dt.getHours()).padStart(2, '0');
    const minutes = String(dt.getMinutes()).padStart(2, '0');
    const seconds = String(dt.getSeconds()).padStart(2, '0');
    const baseSerial = parseInt(`${hours}${minutes}${seconds}`, 10);

    const packedDate = dt.toLocaleDateString();
    const packedTime = dt.toLocaleTimeString();

    let successCount = 0;
    let errorMessage = '';

    for (let i = 0; i < numberOfBoxes; i++) {
      setCurrentBox(i + 1);

      try {
        const serialNumber = String(baseSerial + i).padStart(6, '0');
        const weight = product.unit_weight ?? product.target_weight ?? 0;

        // Generate barcode via product-service API
        const barcode = await barcodeService.generateBarcode(
          product.item_number,
          false,
          weight,
          dateCode,
          serialNumber
        );

        // Build label data
        const label_data = {
          product_name: product.item_english_name || '',
          product_chinese: product.item_chinese_name || '',
          product_sku: product.item_number,
          product_ingredient: product.ingredient || '',
          lot_number: `BATCH-${dateCode}`,
          box_number: i + 1,
          barcode_number: barcode.data,
          actual_weight: Number(weight).toFixed(2),
          target_weight: Number(weight).toFixed(2),
          weight_unit: 'lbs',
          packed_date: packedDate,
          packed_time: packedTime,
          packed_timestamp: dt.toISOString(),
          operator_id: user?.employee_number || user?.id || '',
          operator_name: user?.fullName || user?.full_name || user?.username || '',
          station_number: '',
          station_name: '',
          raw_material_lot: '',
          raw_material_type: '',
          production_location: 'HTE',
          qr_data: barcode.data,
        };

        if (!simulatePrinter) {
          const base = getPrintServiceUrl();
          const url = base ? `${base}/api/print/html-label` : '/api/print/html-label';
          const token = localStorage.getItem('authToken');
          const res = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              label_data,
              barcode_data: barcode.data,
              barcode_display_text: barcode.displayText,
              copies: labelCopies,
              printer_ip: printer.ip,
            }),
          });
          const data = await res.json();
          if (!data.success) {
            errorMessage = getPrintErrorMessage(t, data.error_code, data.error, t('admin.batchPrintFailed'));
            break;
          }
        }

        successCount++;

        // Wait 500ms between boxes to avoid overwhelming the printer
        if (i < numberOfBoxes - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (err: any) {
        errorMessage = err?.message || t('admin.batchPrintFailed');
        break;
      }
    }

    setIsPrinting(false);

    if (errorMessage) {
      setResult({
        success: false,
        message: `${t('admin.batchPrintFailed')} (${successCount}/${numberOfBoxes}): ${errorMessage}`,
      });
    } else {
      const msg = simulatePrinter
        ? t('admin.batchPrintSimulated')
        : t('admin.batchPrintSuccess');
      setResult({
        success: true,
        message: `${msg} — ${totalLabels} ${t('admin.batchPrintLabels')}`,
      });
    }
  };

  return (
    <div className="border border-secondary-200 dark:border-gray-700 rounded-lg p-5">
      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        <Package className="w-5 h-5 text-secondary-500" />
        <h3 className="text-lg font-medium text-secondary-900 dark:text-white">
          {t('admin.batchLabelPrint')}
        </h3>
      </div>
      <p className="text-xs text-secondary-500 dark:text-gray-400 mb-4">
        {t('admin.batchLabelPrintDesc')}
      </p>

      {/* Form fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Product dropdown */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-1">
            {t('admin.batchProduct')}
          </label>
          <select
            value={selectedProduct}
            onChange={(e) => { setSelectedProduct(e.target.value); setResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">{t('admin.batchSelectProduct')}</option>
            {products.map((p: any) => (
              <option key={p.item_number} value={p.item_number}>
                {p.item_number} - {p.item_english_name}{p.item_chinese_name ? ` / ${p.item_chinese_name}` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Number of boxes */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-1">
            {t('admin.batchNumberOfBoxes')}
          </label>
          <input
            type="number"
            value={numberOfBoxes}
            onChange={(e) => { setNumberOfBoxes(Math.max(1, Math.min(999, parseInt(e.target.value) || 1))); setResult(null); }}
            min={1}
            max={999}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Date & Time */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-1">
            {t('admin.batchDateTime')}
          </label>
          <input
            type="datetime-local"
            value={selectedDateTime}
            onChange={(e) => { setSelectedDateTime(e.target.value); setResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Printer dropdown */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 dark:text-gray-300 mb-1">
            {t('admin.batchPrinter')}
          </label>
          <select
            value={selectedPrinter}
            onChange={(e) => { setSelectedPrinter(e.target.value); setResult(null); }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">{t('admin.selectPrinter')}</option>
            {printers.map((p) => (
              <option key={p.key} value={p.key}>{p.nickname}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Info line + Print button */}
      <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
        <p className="text-sm text-secondary-600 dark:text-gray-400">
          {t('admin.batchTotalLabels')}: <span className="font-semibold">{totalLabels}</span>
          {' '}({numberOfBoxes} {t('admin.batchBoxes')} × {labelCopies} {t('admin.batchCopies')})
        </p>

        <button
          onClick={handleBatchPrint}
          disabled={!canPrint}
          className="px-5 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center gap-2"
        >
          <Printer className="w-4 h-4" />
          {isPrinting ? t('admin.batchPrinting') : t('admin.batchPrintButton')}
        </button>
      </div>

      {/* Progress bar */}
      {isPrinting && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-sm text-secondary-600 dark:text-gray-400 mb-1">
            <span>{t('admin.batchPrintingBox')} {currentBox} / {numberOfBoxes}...</span>
            <span>{((currentBox / numberOfBoxes) * 100).toFixed(2)}%</span>
          </div>
          <div className="w-full bg-secondary-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentBox / numberOfBoxes) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Result indicator */}
      {result && !isPrinting && (
        <div className={`mt-3 flex items-center gap-2 text-sm ${result.success ? 'text-green-600' : 'text-red-500'}`}>
          {result.success ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {result.message}
        </div>
      )}
    </div>
  );
};

export default BatchLabelPrintSection;
